import {Button, DateField, TextField, view, FormFieldGroup} from '@sail/ui';
import {LightCard} from '@stripe-internal/conversations-ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl, defineMessages} from 'react-intl';
import {loadStripe} from '@stripe/stripe-js';
import {
  CardNumberElement,
  CardExpiryElement,
  Elements,
  ElementsConsumer,
} from '@stripe/react-stripe-js';
import type {Stripe, StripeElements} from '@stripe/stripe-js';
import useFieldState from './useFieldState';
import {
  getAmountErrors,
  getDateErrors,
  inCents,
  getStripePublishableKey,
} from './formUtils';

const messages = defineMessages({
  errorInvalidAmount: {
    id: 'mse_inline_data.charge_lookup_form.errorInvalidAmount',
    description: 'An error message for when the charge amount is invalid',
    defaultMessage: 'Invalid amount',
  },
  errorInvalidDate: {
    id: 'mse_inline_data.charge_lookup_form.errorInvalidDate',
    description: 'An error message for when the date value is invalid',
    defaultMessage: 'Invalid date',
  },
  formTitle: {
    id: 'mse_inline_data.charge_lookup_form.formTitle',
    description: 'The title of the charge lookup form',
    defaultMessage: 'Charge lookup',
  },
  fieldLabelChargeAmount: {
    id: 'mse_inline_data.charge_lookup_form.fieldLabelChargeAmount',
    description:
      'The label on the charge lookup field for the amount of the charge',
    defaultMessage: 'Amount',
  },
  fieldPlaceholderChargeAmount: {
    id: 'mse_inline_data.charge_lookup_form.fieldPlaceholderChargeAmount',
    description:
      'The placeholder on the charge lookup field for the amount of the charge',
    defaultMessage: '12.34',
  },
  fieldLabelChargeDate: {
    id: 'mse_inline_data.charge_lookup_form.fieldLabelChargeDate',
    description:
      'The label on the charge lookup field for the date of the charge',
    defaultMessage: 'Date',
  },
  fieldLabelChargedCard: {
    id: 'mse_inline_data.charge_lookup_form.fieldLabelChargedCard',
    description: 'The label on the charge lookup field for the card number',
    defaultMessage: 'Card information',
  },
  fieldPlaceholderChargedCard: {
    id: 'mse_inline_data.charge_lookup_form.fieldPlaceholderChargedCard',
    description:
      'The placeholder on the charge lookup field for the card number',
    defaultMessage: 'Card number',
  },
  submitText: {
    id: 'mse_inline_data.charge_lookup_form.submitText',
    description:
      'Text on the button in the charge lookup that performs the lookup',
    defaultMessage: 'Submit',
  },
});

export type ValueProps = {
  charge_amount?: string;
  charge_date?: string;
  charged_card?: string;
};

// Component for Amount Field
interface AmountFieldProps {
  value: string;
  touched: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
}

const AmountField: React.FC<AmountFieldProps> = ({
  value,
  touched,
  onChange,
  onBlur,
}) => {
  const intl = useIntl();

  // ensures more than one decimal or alphabets are not accepted from the user
  const handleAmountChange = (inputValue: string) => {
    let parsedAmt = inputValue.replace(/[^0-9.]/g, '');

    const decimalIndex = parsedAmt.indexOf('.');
    if (decimalIndex !== -1) {
      parsedAmt =
        parsedAmt.substring(0, decimalIndex + 1) +
        parsedAmt.substring(decimalIndex + 1).replace(/\./g, '');
    }

    onChange(parsedAmt);
  };

  return (
    <FormFieldGroup name="chargeAmount" size="medium">
      <TextField
        id="charge-amount-field"
        label={intl.formatMessage(messages.fieldLabelChargeAmount)}
        placeholder={intl.formatMessage(messages.fieldPlaceholderChargeAmount)}
        maxLength={15}
        width="maximized"
        invalid={
          touched && !!getAmountErrors(value, intl, messages.errorInvalidAmount)
        }
        error={
          touched && getAmountErrors(value, intl, messages.errorInvalidAmount)
        }
        value={value}
        onChange={(e) => handleAmountChange(e.target.value)}
        onBlur={onBlur}
      />
    </FormFieldGroup>
  );
};

// Component for Charge Date Field using simple direct approach
interface ChargeDateFieldProps {
  day: string;
  month: string;
  year: string;
  dayTouched: boolean;
  monthTouched: boolean;
  yearTouched: boolean;
  onDayChange: (day: string) => void;
  onMonthChange: (month: string) => void;
  onYearChange: (year: string) => void;
  onDayBlur: () => void;
  onMonthBlur: () => void;
  onYearBlur: () => void;
}

const ChargeDateField: React.FC<ChargeDateFieldProps> = ({
  day,
  month,
  year,
  dayTouched,
  monthTouched,
  yearTouched,
  onDayChange,
  onMonthChange,
  onYearChange,
  onDayBlur,
  onMonthBlur,
  onYearBlur,
}) => {
  const intl = useIntl();

  const dateFieldTouched = dayTouched && monthTouched && yearTouched;

  return (
    <FormFieldGroup name="chargeDate" size="medium">
      <DateField
        id="charge-date-field"
        label={intl.formatMessage(messages.fieldLabelChargeDate)}
        format="MDY"
        invalid={
          dateFieldTouched &&
          !!getDateErrors(day, month, year, intl, messages.errorInvalidDate)
        }
        error={
          dateFieldTouched &&
          getDateErrors(day, month, year, intl, messages.errorInvalidDate)
        }
        value={`${year}-${month}-${day}`}
        onChange={(date) => {
          const [inputYear, inputMonth, inputDay] = date.split('-');
          onYearChange(inputYear);
          onMonthChange(inputMonth);
          onDayChange(inputDay);
        }}
        onBlur={() => {
          onYearBlur();
          onMonthBlur();
          onDayBlur();
        }}
      />
    </FormFieldGroup>
  );
};

// Component for Card Input Field
interface CardInputFieldProps {
  cardError: string | null;
  cardTouched: boolean;
  onCardComplete: (complete: boolean) => void;
  onCardError: (error: string | null) => void;
  onCardTouched: (touched: boolean) => void;
}

const CardInputField: React.FC<CardInputFieldProps> = ({
  cardError,
  cardTouched,
  onCardComplete,
  onCardError,
  onCardTouched,
}) => {
  const intl = useIntl();

  const [cardFocused, setCardFocused] = React.useState(false);

  const handleCardChange = useCallback(
    (event: {error?: {message: string}; complete: boolean}) => {
      onCardError(event.error?.message ?? null);
      onCardComplete(event.complete);
      if (event.error || event.complete) {
        onCardTouched(true);
      }
    },
    [onCardComplete, onCardError, onCardTouched],
  );

  const handleCardFocus = useCallback(() => setCardFocused(true), []);
  const handleCardBlur = useCallback(() => setCardFocused(false), []);

  return (
    <>
      <FormFieldGroup
        legend={intl.formatMessage(messages.fieldLabelChargedCard)}
        name="chargedCard"
        size="medium"
        error={cardTouched && cardError}
      >
        <view.div
          css={{
            border: '1px solid #D8DEE4',
            borderRadius: '7px',
            padding: '6px',
            paddingLeft: '8px',
            backgroundColor: '#FFFFFF',
            boxShadow: cardFocused
              ? '0px 0px 0px 1px rgba(58, 151, 212, 0.28), 0px 0px 0px 4px rgba(58, 151, 212, 0.36), 0px 0px 0px 0px rgba(0, 0, 0, 0)'
              : undefined,
          }}
        >
          <CardNumberElement
            id="charged-card-field"
            onChange={handleCardChange}
            onFocus={handleCardFocus}
            onBlur={handleCardBlur}
            options={{
              style: {
                base: {
                  fontSize: '14px',
                  color: '#353A44',
                  '::placeholder': {
                    color: '#818DA0',
                    fontWeight: '300',
                  },
                  fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
                  fontWeight: '300',
                },
              },
              placeholder: intl.formatMessage(
                messages.fieldPlaceholderChargedCard,
              ),
            }}
          />
        </view.div>
      </FormFieldGroup>

      {/* Hidden expiry element needed for tokenization */}
      <div style={{display: 'none'}}>
        <CardExpiryElement />
      </div>
    </>
  );
};

// Helper function to initialize Stripe expiry element
const initializeCardExpiryElement = (elements: StripeElements | null): void => {
  if (!elements) return;

  const currentDate = new Date();
  currentDate.setFullYear(currentDate.getFullYear() + 1);
  const expiryMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
  const expiryYear = String(currentDate.getFullYear()).slice(-2);
  const cardExpiryDate = `${expiryMonth}/${expiryYear}`;

  const cardExpiryElement = elements.getElement(CardExpiryElement);
  if (cardExpiryElement) {
    cardExpiryElement.update({
      __privateValue: cardExpiryDate,
    } as Record<string, string>);
  }
};

// Helper function to validate form fields
const isFormValid = (
  day: string,
  month: string,
  year: string,
  amount: string | null,
  cardComplete: boolean,
  cardError: string | null,
  intl: ReturnType<typeof useIntl>,
): boolean => {
  return (
    !!day &&
    !!month &&
    !!year &&
    amount != null &&
    !getAmountErrors(amount, intl, messages.errorInvalidAmount) &&
    !getDateErrors(day, month, year, intl, messages.errorInvalidDate) &&
    cardComplete &&
    !cardError
  );
};

// Helper function to prepare submit data
const prepareSubmitData = (
  amount: string,
  day: string,
  month: string,
  year: string,
  tokenId: string,
) => {
  return {
    charge_amount: amount && String(inCents(amount)),
    charge_date: `${year}-${month}-${day}`,
    charged_card: tokenId,
  };
};

// Inner component that uses Stripe Elements
function ChargeLookupFormInner(
  props: InlineDatumReactComponentProps & {
    stripe: Stripe | null;
    elements: StripeElements | null;
  },
) {
  const {onDone, isDone, stripe, elements} = props;

  const intl = useIntl();
  const analytics = useAnalytics();

  // Amount State
  const [amount, amountTouched, setAmount, setAmountTouched] =
    useFieldState('');

  // Date State
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [dayTouched, setDayTouched] = useState(false);
  const [monthTouched, setMonthTouched] = useState(false);
  const [yearTouched, setYearTouched] = useState(false);

  // Card State
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardTouched, setCardTouched] = useState(false);

  // Form State
  const [isFinished, setIsFinished] = useState<boolean>();

  const [isEditing, setIsEditing] = useState<boolean>(true);

  const pageState = useMemo(
    () => ({
      isEditing,
      hasAllRequiredFields: Boolean(
        amount &&
          day &&
          month &&
          year &&
          !getDateErrors(day, month, year, intl, messages.errorInvalidDate) &&
          cardComplete,
      ),
    }),
    [isEditing, amount, day, month, year, cardComplete, intl],
  );

  // Handle form completion state
  useEffect(() => {
    setIsFinished(isDone || false);
    setIsEditing(!isDone);
  }, [isDone]);

  useEffect(() => {
    initializeCardExpiryElement(elements);
  }, [elements]);

  const handleSubmit = async () => {
    setAmountTouched();
    setDayTouched(true);
    setMonthTouched(true);
    setYearTouched(true);
    setCardTouched(true);

    if (!isFormValid(day, month, year, amount, cardComplete, cardError, intl)) {
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      return;
    }

    try {
      const {error, token} = await stripe.createToken(cardNumberElement);
      if (error) {
        setCardError(
          error.message ??
            'An error occurred while processing the card information',
        );
        return;
      }

      const submitData = prepareSubmitData(amount, day, month, year, token.id);

      setIsEditing(false);
      setIsFinished(true);

      // passing data to the user_input prop in backend handler branch type
      onDone({
        type: 'success',
        message: 'Charge Lookup Form Data',
        extra: {
          outcome: JSON.stringify(submitData),
        },
      });

      analytics.action('charge_lookup_form_submit_click', pageState);
    } catch (err) {
      setCardError('An error occurred while processing the card information');
    }
  };

  return (
    <>
      {isEditing ? (
        <>
          <view.div css={{marginTop: 'small', width: 'fill'}}>
            <LightCard isOuter>
              <view.div css={{gap: 'medium', stack: 'y'}}>
                <view.div
                  css={{margin: 0, font: 'heading.small', display: 'flex'}}
                >
                  {intl.formatMessage(messages.formTitle)}
                </view.div>

                <view.div css={{stack: 'y', gap: 'medium'}}>
                  <AmountField
                    value={amount}
                    touched={amountTouched}
                    onChange={setAmount}
                    onBlur={setAmountTouched}
                  />

                  <ChargeDateField
                    day={day}
                    month={month}
                    year={year}
                    dayTouched={dayTouched}
                    monthTouched={monthTouched}
                    yearTouched={yearTouched}
                    onDayChange={setDay}
                    onMonthChange={setMonth}
                    onYearChange={setYear}
                    onDayBlur={() => setDayTouched(true)}
                    onMonthBlur={() => setMonthTouched(true)}
                    onYearBlur={() => setYearTouched(true)}
                  />

                  <CardInputField
                    cardError={cardError}
                    cardTouched={cardTouched}
                    onCardComplete={setCardComplete}
                    onCardError={setCardError}
                    onCardTouched={setCardTouched}
                  />

                  {!isFinished && (
                    <>
                      <Button
                        css={{width: 'fill'}}
                        data-testid="continue-btn"
                        type="primary"
                        onPress={handleSubmit}
                        disabled={!pageState.hasAllRequiredFields}
                      >
                        <FormattedMessage {...messages.submitText} />
                      </Button>
                    </>
                  )}
                </view.div>
              </view.div>
            </LightCard>
          </view.div>
        </>
      ) : null}
    </>
  );
}

const InjectedChargeLookupForm = (props: InlineDatumReactComponentProps) => {
  return (
    <ElementsConsumer>
      {({elements, stripe}) => (
        <ChargeLookupFormInner {...props} elements={elements} stripe={stripe} />
      )}
    </ElementsConsumer>
  );
};

const stripePromise = loadStripe(getStripePublishableKey());

export const ChargeLookupForm = (props: InlineDatumReactComponentProps) => {
  if (!stripePromise) {
    return null;
  }
  return (
    <Elements stripe={stripePromise}>
      <InjectedChargeLookupForm {...props} />
    </Elements>
  );
};
